/**
 * ProfileQrCard — shareable QR code for a public profile URL.
 * Used on the user's own Account page and on any PublicProfile.
 */
import { useMemo, useState } from "react";
import { Copy, Download, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import QrCodeImage from "@/components/events/QrCodeImage";

interface Props {
  /** profiles.profile_id (public-facing slug used in /u/:profileId). */
  profileId: string;
  displayName?: string | null;
  size?: number;
}

const ProfileQrCard = ({ profileId, displayName, size = 200 }: Props) => {
  const [copied, setCopied] = useState(false);

  const url = useMemo(() => {
    const origin =
      typeof window !== "undefined" ? window.location.origin : "https://wavlynq.com";
    return `${origin}/u/${profileId}`;
  }, [profileId]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // ignore — some browsers block without https
    }
  };

  const download = async () => {
    const QRCode = (await import("qrcode")).default;
    const dataUrl = await QRCode.toDataURL(url, {
      margin: 2,
      width: 1024,
      errorCorrectionLevel: "M",
    });
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `wavlynq-${profileId}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5 flex flex-col items-center gap-4">
      <QrCodeImage value={url} size={size} alt={`QR code to ${displayName ?? "profile"}`} />
      <div className="text-center">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
          Scan to view
        </p>
        <p className="mt-1 text-sm font-medium break-all">{url}</p>
      </div>
      <div className="flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={copy} className="gap-2">
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          {copied ? "Copied" : "Copy link"}
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={download} className="gap-2">
          <Download className="h-4 w-4" />
          Download
        </Button>
      </div>
    </div>
  );
};

export default ProfileQrCard;
